/**
 * stepUp.ts - the `returnTo` round trip for an interactive re-authentication.
 *
 * WHAT THIS USED TO BE. The destructive operator routes required the token's `auth_time` to be
 * under ten minutes old, and `requestStepUp` drove the `prompt=login` redirect that was supposed
 * to refresh it.
 *
 * THAT CHECK WAS REMOVED ON 2026-10-29, because Authentik cannot satisfy it. The `Terramantle App`
 * provider authorizes through a flow with no login stage, so `prompt=login` sent with a live SSO
 * session returns a code WITHOUT re-authenticating: the authentik event log shows the authorize
 * carrying `"prompt": "login"` in its request args with no `login` event beside it, and the new
 * token carrying the same `auth_time` as the old one. The console's "Re-authenticate" button
 * therefore looped forever. See packages/worker/src/lib/platform/destructiveGuard.ts.
 *
 * WHAT IS LEFT, and why the file stays: `safeReturnTo` and `returnToFromState` are what
 * app/Callback.tsx uses to send a signed-in user back where they started instead of to the
 * dashboard, which has nothing to do with step-up. `requestStepUp` is kept because it is the
 * working half of a reinstatement - fix the IdP flow, and the console needs this unchanged.
 */
import { userManager } from '@/auth/config'

/** The shape handed to signinRedirect and read back in the callback. */
export interface StepUpState {
  returnTo: string
}

/**
 * Only same-origin paths are honoured on return. A `returnTo` is attacker-influencable in general
 * (it is just state), and `//evil.example` is a protocol-relative URL that a naive check treats as
 * a path - which is an open redirect.
 */
export function safeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/') || value.startsWith('//')) return null
  return value
}

/**
 * Re-authenticates, then comes back to `returnTo`. Returns only if the redirect could not be
 * started; otherwise the page is navigating away by the time it resolves.
 *
 * NO CURRENT CALLERS - see the header. `prompt=login` does not force a fresh authentication
 * against this Authentik provider, so anything relying on it to move `auth_time` will loop.
 */
export async function requestStepUp(returnTo: string): Promise<void> {
  const state: StepUpState = { returnTo }
  await userManager.signinRedirect({
    extraQueryParams: { prompt: 'login' },
    state,
  })
}

/** Reads the `returnTo` out of whatever signinRedirectCallback handed back. */
export function returnToFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null
  return safeReturnTo((state as { returnTo?: unknown }).returnTo)
}
